import React from 'react';
/**
 * wHANDY 구매 확인 팝업
 *
 * 왜 한 단계를 더 두나
 * --------------------
 * 구매하면 받는 wHANDY 에 락업이 걸린다(거치 후 매월 분할 해제). 산 사람이
 * "바로 쓸 수 있는 줄 알았다"고 하면 되돌릴 방법이 없다 — 온체인 금고에 박히기
 * 때문이다. 그래서 버튼 한 번으로 바로 사지지 않게 하고, 락업 일정과 취소 불가를
 * 명시한 뒤 동의를 받아야만 확정되게 한다.
 *
 * 락업 조건(거치 개월·분할 횟수)은 어드민 설정값을 서버에서 받아 그대로 표시한다.
 * 화면에 숫자를 박아두면 설정을 바꿨을 때 안내와 실제가 어긋난다.
 */
import { useState, useMemo } from 'react';
import { X, Lock, AlertTriangle } from 'lucide-react';

interface LockPolicy { enabled: boolean; cliffMonths: number; splitCount: number; }

interface Props {
  open: boolean;
  onClose: () => void;
  usdtBalance: number;
  depositAddress?: string;
  price: number | null;
  feePercent: number;
  lock: LockPolicy | null;
  onConfirm: (usdtAmount: number) => Promise<void>;
}

export function BuyConfirmModal({ open, onClose, usdtBalance, depositAddress, price, feePercent, lock, onConfirm }: Props) {
  const [copied, setCopied] = React.useState(false);
  const [amount, setAmount] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const usdt = Number(amount);
  const valid = Number.isFinite(usdt) && usdt > 0 && usdt <= usdtBalance;

  // 받을 수량 = (낸 금액 / 시세) 에서 수수료를 뺀 값. 서버 계산과 같은 식이다.
  const handy = useMemo(() => {
    if (!valid || !price) return 0;
    const gross = usdt / price;
    return gross - gross * ((feePercent || 0) / 100);
  }, [usdt, price, feePercent, valid]);

  const locked = Boolean(lock?.enabled && (lock?.cliffMonths ?? 0) > 0);
  const cliff = lock?.cliffMonths ?? 0;
  const split = lock?.splitCount ?? 1;
  const perRelease = handy > 0 ? handy / split : 0;
  const pct = Math.round(100 / split);

  const schedule = useMemo(() => {
    if (!locked || handy <= 0) return [];
    const out: { date: string; amount: number }[] = [];
    for (let i = 0; i < split; i++) {
      const d = new Date();
      d.setMonth(d.getMonth() + cliff + i);
      out.push({ date: d.toISOString().slice(0, 10), amount: perRelease });
    }
    return out;
  }, [locked, handy, cliff, split, perRelease]);

  if (!open) return null;

  const submit = async () => {
    if (!valid || !agreed || busy) return;
    setBusy(true);
    try {
      await onConfirm(usdt);
      setAmount(''); setAgreed(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-background rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-auto">
        <div className="sticky top-0 bg-background flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-lg">wHANDY 구매</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted" aria-label="닫기">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* USDT 입금 주소. [2026-10-17] 이게 없어서 USDT 가 0 인 회원은
              구매창을 열어도 할 수 있는 게 없었다 — 어디로 보내야 하는지
              화면 어디에도 안 적혀 있었다. */}
          {depositAddress && (
            <div className="rounded-xl p-4"
                 style={{ background: 'rgba(255,107,0,.08)', border: '1px solid rgba(255,107,0,.25)' }}>
              <div className="text-sm font-bold mb-1">① USDT 입금 주소</div>
              <div className="text-xs text-muted-foreground mb-2" style={{ lineHeight: 1.6 }}>
                아래 주소로 <b>USDT (BEP20 · BNB 스마트체인)</b> 를 보내세요.
                <br />입금은 <b>5분 안에</b> 자동으로 잔액에 반영됩니다.
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs break-all px-3 py-2.5 rounded-lg"
                      style={{ background: 'rgba(0,0,0,.06)', lineHeight: 1.5 }}>
                  {depositAddress}
                </code>
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(depositAddress);
                      setCopied(true); setTimeout(() => setCopied(false), 1500);
                    } catch {}
                  }}
                  className="px-3 py-2.5 rounded-lg text-xs font-bold shrink-0"
                  style={{ background: '#FF6B00', color: '#fff', border: 0 }}>
                  {copied ? '복사됨' : '복사'}
                </button>
              </div>
              <div className="text-xs mt-2" style={{ color: '#DC2626', lineHeight: 1.6 }}>
                ⚠ 다른 체인(ERC20·TRC20)으로 보내면 복구할 수 없습니다.
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-muted-foreground">
                {depositAddress ? '② 구매 금액 (USDT)' : '구매 금액 (USDT)'}
              </label>
              <button onClick={() => setAmount(String(usdtBalance))}
                className="text-xs font-semibold text-primary">
                전액 {usdtBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}
              </button>
            </div>
            <input
              value={amount}
              onChange={e => { setAmount(e.target.value); setAgreed(false); }}
              inputMode="decimal"
              placeholder="0"
              className="w-full px-4 py-3 rounded-xl bg-muted text-lg font-bold outline-none"
            />
            {amount !== '' && !valid && (
              <p className="text-xs mt-1 text-red-500">
                {usdt > usdtBalance ? '보유 USDT 를 초과했습니다.' : '올바른 금액을 입력해주세요.'}
              </p>
            )}
          </div>

          <div className="rounded-xl bg-muted p-4 space-y-1.5">
            <Row label="적용 시세" value={price ? `$${price}` : '조회 중…'} />
            <Row label="수수료" value={`${feePercent || 0}%`} />
            <Row label="받을 수량" value={handy > 0 ? `${handy.toLocaleString(undefined, { maximumFractionDigits: 4 })} wHANDY` : '-'} bold />
          </div>

          {locked && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4 text-amber-600" />
                <p className="font-bold text-amber-900 text-sm">락업 안내</p>
              </div>
              <p className="text-sm text-amber-900 leading-relaxed">
                구매한 wHANDY 는 <b>{cliff}개월간 사용할 수 없습니다.</b><br />
                {cliff + 1}개월차부터 매월 <b>{pct}%씩 {split}회</b>에 걸쳐 해제됩니다.
              </p>
              {schedule.length > 0 && (
                <div className="mt-3 space-y-1">
                  {schedule.map(r => (
                    <div key={r.date} className="flex justify-between text-xs text-amber-900">
                      <span>{r.date}</span>
                      <span className="font-semibold">
                        {r.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-red-300 bg-red-50 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 leading-relaxed">
                <b>구매 후 취소·환불이 불가능합니다.</b><br />
                확정하면 되돌릴 수 없습니다. 금액과 락업 일정을 다시 확인해주세요.
              </p>
            </div>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span className="text-sm">
              위 락업 일정과 <b>취소·환불 불가</b>에 동의합니다.
            </span>
          </label>

          <button
            onClick={submit}
            disabled={!valid || !agreed || busy}
            className="w-full py-3.5 rounded-xl font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#FF6B00' }}
          >
            {busy ? '처리 중…' : '구매 확정'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? 'font-bold' : ''}>{value}</span>
    </div>
  );
}
